import {
  closestCenter,
  DndContext,
  DragOverlay,
  type DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Card as MUICard,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { httpClient } from 'src/libs';
import {
  createBoard,
  deleteBoard,
  getAllBoards,
  updateBoard,
} from 'src/services/boards';
import { ICard, IColumn, IEditBoardData } from 'src/types/boards-lists';
import { BoardColumn } from '../../components/boardsAndLists/BoardColumn';
import { BoardLoading } from '../../components/boardsAndLists/BoardLoading';
import ConfirmDeleteDialog from '../../components/boardsAndLists/ConfirmDeleteDialog';
import CreateOrUpdateBoardDialog from '../../components/boardsAndLists/CreateOrUpdateBoardDialog';
import TopSectionBoards from 'src/components/boardsAndLists/TopSectionBoards';

// Skeleton para cards do board (simulando o visual real)
const BoardCardSkeleton: React.FC = () => (
  <Box
    sx={{
      pt: 2,
      minWidth: 380,
      maxWidth: 420,
      width: '100%',
      bgcolor: 'background.paper',
      borderRadius: 3,
      boxShadow: 1,
      p: 3,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Skeleton variant="text" width="60%" height={36} sx={{ mb: 0.5 }} />
    <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
    <Skeleton variant="text" width="90%" height={18} sx={{ mb: 2 }} />
    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
      <Skeleton variant="rounded" width={70} height={28} />
      <Skeleton variant="rounded" width={100} height={28} />
      <Skeleton variant="rounded" width={120} height={28} />
    </Box>
    <Skeleton variant="rectangular" width="100%" height={1} sx={{ mb: 2 }} />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
      <Skeleton variant="text" width={80} height={18} />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Skeleton variant="circular" width={28} height={28} />
      </Box>
    </Box>
  </Box>
);

const ChooseBoard = () => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallScreen = useMediaQuery('(max-width:1500px)');
  const [columns, setColumns] = useState<IColumn[]>([]);
  const [activeCard, setActiveCard] = useState<ICard | null>(null);
  const [editBoard, setEditBoard] = useState<null | IColumn>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [boardToDelete, setBoardToDelete] = useState<IColumn | null>(null);

  const findCard = (id: string | number) => {
    for (const col of columns) {
      const card = col.cards.find((c) => c.id === id);
      if (card) return { card, columnId: col.id } as const;
    }
    return null;
  };

  const handleDragStart = (event: any) => {
    const { active } = event;
    const found = findCard(active.id);
    if (found) setActiveCard(found.card);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveCard(null);
    if (!over) return;

    const from = findCard(active.id);
    const to = findCard(over.id);
    const isOverColumn = columns.some((col) => col.id === over.id);

    if (!from) return;

    if (isOverColumn) {
      setColumns((cols) => {
        const fromCol = cols.find((c) => c.id === from.columnId)!;
        const card = fromCol.cards.find((c) => c.id === active.id)!;
        return cols.map((col) => {
          if (col.id === from.columnId)
            return {
              ...col,
              cards: col.cards.filter((c) => c.id !== active.id),
            };
          if (col.id === over.id)
            return { ...col, cards: [...col.cards, card] };
          return col;
        });
      });
      return;
    }

    if (!to) return;

    if (from.columnId === to.columnId) {
      setColumns((cols) =>
        cols.map((col) =>
          col.id === from.columnId
            ? {
                ...col,
                cards: arrayMove(
                  col.cards,
                  col.cards.findIndex((c) => c.id === active.id),
                  col.cards.findIndex((c) => c.id === over.id)
                ),
              }
            : col
        )
      );
    } else {
      setColumns((cols) => {
        const fromCol = cols.find((c) => c.id === from.columnId)!;
        const card = fromCol.cards.find((c) => c.id === active.id)!;
        return cols.map((col) => {
          if (col.id === from.columnId)
            return {
              ...col,
              cards: col.cards.filter((c) => c.id !== active.id),
            };
          if (col.id === to.columnId) {
            const idx = col.cards.findIndex((c) => c.id === over.id);
            const next = [...col.cards];
            next.splice(idx + 1, 0, card);
            return { ...col, cards: next };
          }
          return col;
        });
      });
    }
  };

  const handleAddColumn = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateOrUpdateBoard = async (data: {
    id?: string;
    title: string;
    subtitle: string;
    description: string;
    tags: string[];
  }) => {
    if (data.id) {
      setEditLoading(true);
      try {
        await updateBoard(data.id, {
          title: data.title,
          subtitle: data.subtitle,
          description: data.description,
          tags: data.tags,
        });
        setEditBoard(null);
        fetchData();
      } catch (error) {
        console.error('Error updating board:', error);
      } finally {
        setEditLoading(false);
      }
    } else {
      const newColumn: IColumn = {
        id: `${Date.now()}`,
        title: data.title,
        description: data.description,
        cards: [],
        subtitle: data.subtitle,
        tags: data.tags,
      };
      setColumns((cols) => [...cols, newColumn]);
      setCreateDialogOpen(false);
      try {
        await createBoard(data);
        fetchData();
      } catch (error) {
        setColumns((cols) => cols.filter((col) => col.id !== newColumn.id)); // rollback
        console.error('Erro ao criar board:', error);
      }
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAllBoards({
        page: 1,
        per_page: 20,
      });

      const boards = response.boards || [];
      const mappedColumns: IColumn[] = boards.map((b: any) => ({
        id: b.id,
        title: b.title,
        subtitle: b.subtitle || '',
        tags: b.tags || [],
        description: b.description || '',
        cards: [],
      }));
      setColumns(mappedColumns);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditBoard = (column: IColumn) => {
    setEditBoard(column);
  };

  const handleSaveEditBoard = async (data: IEditBoardData) => {
    if (!editBoard) return;
    setEditLoading(true);
    try {
      await httpClient.patch(
        `${process.env.REACT_APP_BASE_URL}/comms-api/boards/${editBoard.id}`,
        {
          title: data.title,
          subtitle: data.subtitle,
          description: data.description,
          tags: data.tags,
        },
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json',
          },
        }
      );
      setEditBoard(null);
      fetchData();
    } catch (error) {
      console.error('Error updating board:', error);
    } finally {
      setEditLoading(false);
    }
  };

  const handleOpenDeleteBoard = (column: IColumn) => {
    setBoardToDelete(column);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setBoardToDelete(null);
  };

  const handleConfirmDeleteBoard = async () => {
    if (!boardToDelete) return;
    const deletedId = boardToDelete.id;
    setColumns((cols) => cols.filter((col) => col.id !== deletedId));
    setDeleteDialogOpen(false);
    setBoardToDelete(null);
    try {
      await deleteBoard(deletedId); // Implemente essa função no seu serviço
    } catch (error) {
      fetchData(); // rollback
      console.error('Erro ao deletar board:', error);
    }
  };

  const handleAddCard = (columnId: string) => {};

  useEffect(() => {
    fetchData();
  }, []);

  // Styles baseados no ContactsWrapper
  const styles = {
    wrapper: {
      color: theme.palette.mode === 'dark' ? 'grey.100' : 'text.primary',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      p: { xs: 1, sm: 2, md: 1 },
    },
    contentContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative',
      height: '100%',
      p: 2,
    },
    contentWrapper: {
      position: 'relative',
      borderRadius: '8px',
      border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200]}`,
      overflow: 'auto',
      boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      background: theme.palette.background.paper,
      backdropFilter: 'blur(8px)',
      px: { xs: 2, md: 4 },
      py: { xs: 2, md: 0 },
    },
  };

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.contentContainer}>
        <Box sx={styles.contentWrapper}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: theme.palette.background.paper,
              marginX: { xs: -2, md: -4 },
              paddingX: { xs: 2, md: 4 },
            }}
          >
            <TopSectionBoards
              title="My Boards"
              description="Create boards to organize your tasks and projects."
              loading={loading}
              rightAction={
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddColumn}
                  sx={{
                    background: theme.palette.secondary.main,
                    color: '#FFF',
                    textTransform: 'none',
                    px: 4,
                    py: 1.5,
                    '&:hover': { background: theme.palette.secondary.dark },
                  }}
                  variant="contained"
                >
                  Create Board
                </Button>
              }
            />
          </Box>
          {loading && (
            <Box sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              minHeight: 340,
              gap: 4,
              flexWrap: 'wrap',
              mb: 2,
            }}>
              {[...Array(isSmallScreen ? 1 : 3)].map((_, idx) => (
                <BoardCardSkeleton key={idx} />
              ))}
            </Box>
          )}
          <DndContext
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <Grid
              container
              spacing={isSmall ? 2 : 3}
              alignItems="flex-start"
              sx={{ minHeight: '70vh', flexWrap: 'wrap', pb: 4 }}
            >
              {columns.length === 0 && !loading && (
                <Grid item xs={12}>
                  <Typography color="text.secondary" align="center">
                    No boards found.
                  </Typography>
                </Grid>
              )}
              {columns.map((column) => (
                <BoardColumn
                  key={column.id}
                  column={column}
                  isSmall={isSmall}
                  onAddCard={handleAddCard}
                  handleOpenEditBoard={handleOpenEditBoard}
                  handleOpenDeleteBoard={handleOpenDeleteBoard}
                />
              ))}
            </Grid>

            <DragOverlay dropAnimation={null}>
              {activeCard ? (
                <MUICard
                  sx={{
                    opacity: 0.9,
                    minWidth: 200,
                    borderRadius: 1,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                    cursor: 'grabbing',
                  }}
                  variant="outlined"
                >
                  <CardContent sx={{ px: 2, py: 1 }}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {activeCard.content}
                    </Typography>
                  </CardContent>
                </MUICard>
              ) : null}
            </DragOverlay>
          </DndContext>
        </Box>
      </Box>

      {/* Dialogs e overlays continuam fora do contentWrapper */}
      <Dialog
        open={!!editBoard}
        onClose={() => setEditBoard(null)}
        maxWidth="xs"
        fullWidth
        fullScreen={isSmall}
        PaperProps={
          isSmall
            ? {
                sx: {
                  width: '100vw',
                  height: '100vh',
                  maxWidth: '100vw',
                  maxHeight: '100vh',
                  m: 0,
                  borderRadius: 0,
                },
              }
            : {}
        }
      >
        <DialogTitle>Edit Board</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBoard(null)}>Cancel</Button>
          <Button
            onClick={() =>
              handleSaveEditBoard({
                title: editTitle,
                subtitle: editBoard?.subtitle || '',
                description: editDescription,
                tags: editBoard?.tags || [],
              })
            }
            variant="contained"
            disabled={editLoading || !editTitle.trim()}
          >
            {editLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateOrUpdateBoardDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateOrUpdateBoard}
      />

      <CreateOrUpdateBoardDialog
        open={!!editBoard}
        onClose={() => setEditBoard(null)}
        onSubmit={handleCreateOrUpdateBoard}
        initialValues={
          editBoard
            ? {
                id: editBoard.id,
                title: editBoard.title,
                subtitle: editBoard.subtitle || '',
                description: editBoard.description || '',
                tags: editBoard.tags || [],
              }
            : undefined
        }
        isEdit
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        boardName={boardToDelete?.title || ''}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDeleteBoard}
      />
    </Box>
  );
};

export default ChooseBoard;
